import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Logo } from "@/components/landing/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Building2, User, Phone, MessageSquare,
  Briefcase, FileCheck, Sparkles, ShieldCheck, UploadCloud, X, FileText,
  AlertTriangle, Loader2, Mail, Globe, Hash, Info, Home, ChevronRight,
} from "lucide-react";

/* ---------- constants ---------- */
const STEPS = [
  { key: "company",    title: "Company details",      icon: Building2 },
  { key: "person",     title: "Authorized person",    icon: User },
  { key: "number",     title: "WhatsApp number",      icon: Phone },
  { key: "display",    title: "Display name",         icon: MessageSquare },
  { key: "business",   title: "Business details",     icon: Briefcase },
  { key: "documents",  title: "Documents",            icon: FileCheck },
  { key: "confirm",    title: "Confirm & submit",     icon: ShieldCheck },
];

const FREE_EMAIL_DOMAINS = [
  "gmail.com", "yahoo.com", "yahoo.co.in", "outlook.com", "hotmail.com",
  "live.com", "icloud.com", "rediffmail.com", "aol.com", "protonmail.com",
];

const DOC_LABELS = {
  gst_certificate: "GST Certificate",
  msme_certificate: "MSME Certificate",
  certificate_of_incorporation: "Certificate of Incorporation",
  moa: "MOA",
  aoa: "AOA",
  shop_establishment: "Shop & Establishment Certificate",
  trade_license: "Trade License",
  electricity_bill: "Electricity Bill (Company Name)",
  landline_bill: "Landline Bill (Company Name)",
  company_pan: "Company PAN Card",
};
const MANDATORY = ["gst_certificate", "msme_certificate"];

const BUSINESS_CATEGORIES = [
  "E-commerce", "Education", "Finance", "Healthcare", "Hospitality",
  "Logistics", "Marketing & Ads", "Professional Services", "Real Estate",
  "Retail", "SaaS / Technology", "Travel", "Other",
];
const USE_CASES = [
  { v: "marketing", l: "Marketing" },
  { v: "utility", l: "Utility" },
  { v: "authentication", l: "Authentication" },
  { v: "customer_support", l: "Customer Support" },
  { v: "mixed", l: "Mixed" },
];

/* ---------- helpers ---------- */
const isFreeEmail = (email) => {
  const d = (email || "").split("@")[1]?.toLowerCase().trim();
  return !!d && FREE_EMAIL_DOMAINS.includes(d);
};
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
const fmtBytes = (b) => {
  if (!b) return "0 B"; const u = ["B","KB","MB","GB"]; let i=0,n=b;
  while (n>=1024&&i<u.length-1){n/=1024;i++;} return `${n.toFixed(n<10&&i>0?1:0)} ${u[i]}`;
};
const cls = (...a) => a.filter(Boolean).join(" ");

const INITIAL = {
  company_name: "", company_type: "", company_website: "", gst_number: "", company_address: "",
  auth_person_name: "", mobile: "", email: "",
  number_option: "existing", whatsapp_number: "",
  display_name: "",
  business_category: "", business_description: "", facebook_url: "", instagram_url: "",
  has_meta_business_manager: "not_sure", use_case: "mixed",
  confirmed_info: false, authorized: false,
};

/* ---------- small atoms ---------- */
const Field = ({ label, required, error, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-semibold text-slate-800">
      {label} {required && <span className="text-rose-500">*</span>}
    </Label>
    {children}
    {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
    {error && <p className="text-xs text-rose-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {error}</p>}
  </div>
);

const StepHeader = ({ step, total, icon: Icon, title, subtitle }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-emerald-700">
      <Icon className="h-3.5 w-3.5" /> Step {step} of {total}
    </div>
    <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
      {title}
    </h2>
    {subtitle && <p className="mt-1.5 text-slate-600 text-sm sm:text-base">{subtitle}</p>}
  </div>
);

/* ---------- main page ---------- */
export default function WhatsappOnboarding() {
  const nav = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]); // [{id, type, file_name, size, uploading?, error?}]
  const [meta, setMeta] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {ref_id, id}

  useEffect(() => {
    api.get("/onboarding/meta").then(r => setMeta(r.data)).catch(() => {});
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [stepIdx, result]);

  const upd = (patch) => setData((d) => ({ ...d, ...patch }));

  /* ---- step validation ---- */
  const validate = useCallback((idx) => {
    const e = {};
    if (idx === 0) {
      if (!data.company_name.trim() || data.company_name.trim().length < 2) e.company_name = "Company name is required";
      if (!data.company_type) e.company_type = "Choose company type";
      if (!data.company_website.trim()) e.company_website = "Website is required";
      else if (!/^https?:\/\/.+\..+/.test(data.company_website.trim()) && !/^[a-z0-9-]+\.[a-z]{2,}/i.test(data.company_website.trim()))
        e.company_website = "Enter a valid URL";
    }
    if (idx === 1) {
      if (!data.auth_person_name.trim()) e.auth_person_name = "Name is required";
      if (!/^[0-9+\-\s()]{6,20}$/.test(data.mobile.trim())) e.mobile = "Enter a valid mobile number";
      if (!isValidEmail(data.email)) e.email = "Enter a valid email";
    }
    if (idx === 2) {
      if (data.number_option === "existing" && !/^[0-9+\-\s()]{6,20}$/.test((data.whatsapp_number || "").trim()))
        e.whatsapp_number = "Enter a valid WhatsApp number";
    }
    if (idx === 3) {
      if (!data.display_name.trim() || data.display_name.trim().length < 2) e.display_name = "Display name is required";
    }
    if (idx === 5) {
      const types = files.filter(f => !f.error && f.id).map(f => f.type);
      if (types.length < 3) e._docs = "Please upload at least 3 documents";
      else if (!types.some(t => MANDATORY.includes(t))) e._docs = "GST Certificate or MSME Certificate is mandatory";
    }
    if (idx === 6) {
      if (!data.confirmed_info) e.confirmed_info = "Required";
      if (!data.authorized) e.authorized = "Required";
    }
    return e;
  }, [data, files]);

  const next = () => {
    const e = validate(stepIdx);
    setErrors(e);
    if (Object.keys(e).length === 0) setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  };
  const back = () => setStepIdx((i) => Math.max(0, i - 1));

  /* ---- file handling ---- */
  const handleFiles = (incoming, type) => {
    if (!type) { toast.error("Choose document type first"); return; }
    const list = Array.from(incoming).slice(0, 5);
    list.forEach((file) => {
      const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
      const allowed = meta?.allowed_exts || [".pdf",".png",".jpg",".jpeg",".webp"];
      if (!allowed.includes(ext)) { toast.error(`${file.name}: unsupported file type`); return; }
      const maxBytes = (meta?.max_file_mb || 10) * 1024 * 1024;
      if (file.size > maxBytes) { toast.error(`${file.name}: exceeds 10 MB`); return; }
      const localId = Math.random().toString(36).slice(2);
      setFiles((arr) => [...arr, { localId, type, file_name: file.name, size: file.size, uploading: true }]);
      const fd = new FormData(); fd.append("type", type); fd.append("file", file);
      api.post("/onboarding/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })
        .then((r) => {
          const res = r.data;
          setFiles((arr) => arr.map((x) => x.localId === localId ? { ...x, uploading: false, id: res.id } : x));
        })
        .catch((err) => {
          const msg = err?.response?.data?.detail || err.message || "Upload failed";
          setFiles((arr) => arr.map((x) => x.localId === localId ? { ...x, uploading: false, error: msg } : x));
          toast.error(`${file.name}: ${msg}`);
        });
    });
  };

  const removeFile = (localId) => setFiles((arr) => arr.filter((x) => x.localId !== localId));

  /* ---- submit ---- */
  const submit = async () => {
    const e = validate(6);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        gst_number: data.gst_number || null,
        company_address: data.company_address || null,
        business_category: data.business_category || null,
        business_description: data.business_description || null,
        facebook_url: data.facebook_url || null,
        instagram_url: data.instagram_url || null,
        whatsapp_number: data.number_option === "existing" ? data.whatsapp_number : null,
        document_ids: files.filter(f => f.id && !f.error).map(f => f.id),
      };
      const r = await api.post("/onboarding/submit", payload);
      setResult(r.data);
      toast.success("Application submitted!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <SuccessView refId={result.ref_id} onHome={() => nav("/")} />;

  const stepKey = STEPS[stepIdx].key;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="onboarding-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-slate-200/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline-flex items-center text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
              WhatsApp Onboarding
            </span>
          </div>
          <div className="text-xs text-slate-500 hidden sm:block">Need help? <a href="mailto:hello@wappit.in" className="text-emerald-700 font-semibold hover:underline">hello@wappit.in</a></div>
        </div>
      </header>

      {/* Hero strip */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950 text-white" />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14 text-white">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" /> Production-ready onboarding
          </div>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Activate your WhatsApp Business API in 7 simple steps.
          </h1>
          <p className="mt-3 max-w-2xl text-slate-300 text-sm sm:text-base">
            Submit your company details and documents. Our onboarding team typically completes Meta verification within 24&ndash;72 hours.
          </p>
        </div>
      </section>

      {/* Stepper + form */}
      <main className="relative max-w-6xl mx-auto px-5 sm:px-8 py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        {/* Side stepper */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <ol className="flex overflow-x-auto no-scrollbar gap-1.5 pb-1 lg:flex-col lg:overflow-visible" data-testid="onboarding-stepper">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = i < stepIdx;
              const active = i === stepIdx;
              return (
                <li key={s.key} className={cls(
                  "flex items-center gap-2 rounded-xl px-3 py-2 transition-colors flex-shrink-0 lg:flex-shrink",
                  active ? "bg-slate-900 text-white" : done ? "bg-emerald-50 text-emerald-800" : "bg-white text-slate-600 border border-slate-200"
                )}>
                  <span className={cls(
                    "h-7 w-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0",
                    active ? "bg-emerald-400 text-slate-900" : done ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"
                  )}>
                    {done ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-3.5 w-3.5" />}
                  </span>
                  <span className="hidden lg:block text-xs font-semibold whitespace-nowrap">{s.title}</span>
                  <span className="lg:hidden text-[10px] font-bold whitespace-nowrap">{i + 1}</span>
                </li>
              );
            })}
          </ol>
          {/* Progress bar */}
          <div className="mt-3 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all" style={{ width: `${((stepIdx+1)/STEPS.length)*100}%` }} />
          </div>
        </aside>

        {/* Form card */}
        <section className="rounded-3xl bg-white border border-slate-200 shadow-[0_8px_30px_rgb(2,6,23,0.04)] p-6 sm:p-9">
          {stepKey === "company" && <StepCompany data={data} upd={upd} errors={errors} meta={meta} />}
          {stepKey === "person" && <StepPerson data={data} upd={upd} errors={errors} />}
          {stepKey === "number" && <StepNumber data={data} upd={upd} errors={errors} />}
          {stepKey === "display" && <StepDisplay data={data} upd={upd} errors={errors} />}
          {stepKey === "business" && <StepBusiness data={data} upd={upd} errors={errors} />}
          {stepKey === "documents" && (
            <StepDocuments
              files={files}
              onAdd={handleFiles}
              onRemove={removeFile}
              errors={errors}
              meta={meta}
            />
          )}
          {stepKey === "confirm" && <StepConfirm data={data} files={files} upd={upd} errors={errors} />}

          {/* nav */}
          <div className="mt-8 flex items-center justify-between gap-3 border-t border-slate-100 pt-6">
            <Button variant="outline" disabled={stepIdx === 0} onClick={back} className="rounded-full" data-testid="onboarding-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
            <div className="text-xs text-slate-500 hidden sm:block">
              {stepIdx + 1} / {STEPS.length} · {STEPS[stepIdx].title}
            </div>
            {stepIdx < STEPS.length - 1 ? (
              <Button onClick={next} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="onboarding-next-btn">
                Continue <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="onboarding-submit-btn">
                {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}
                Submit application
              </Button>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

/* ---------- STEPS ---------- */
function StepCompany({ data, upd, errors, meta }) {
  return (
    <>
      <StepHeader step={1} total={7} icon={Building2} title="Tell us about your company"
        subtitle="We use this to draft your Meta Business application." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Company name" required error={errors.company_name}>
          <Input value={data.company_name} onChange={(e)=>upd({company_name:e.target.value})}
            placeholder="ABC Technologies Pvt. Ltd." className="h-11 rounded-xl" data-testid="company-name-input" />
        </Field>
        <Field label="Company type" required error={errors.company_type}>
          <Select value={data.company_type} onValueChange={(v)=>upd({company_type:v})}>
            <SelectTrigger className="h-11 rounded-xl" data-testid="company-type-select"><SelectValue placeholder="Select company type" /></SelectTrigger>
            <SelectContent>
              {(meta?.company_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Company website" required error={errors.company_website}>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={data.company_website} onChange={(e)=>upd({company_website:e.target.value})}
              placeholder="https://yourcompany.com" className="h-11 rounded-xl pl-9" data-testid="company-website-input" />
          </div>
        </Field>
        <Field label="GST number" hint="Optional but recommended for faster verification">
          <div className="relative">
            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={data.gst_number} onChange={(e)=>upd({gst_number:e.target.value.toUpperCase()})}
              placeholder="22AAAAA0000A1Z5" className="h-11 rounded-xl pl-9 uppercase" data-testid="gst-input" />
          </div>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Company address" hint="As mentioned on incorporation / GST documents.">
            <Textarea value={data.company_address} onChange={(e)=>upd({company_address:e.target.value})}
              placeholder="Building, street, city, state, pincode" className="rounded-xl min-h-[88px]" data-testid="company-address-input" />
          </Field>
        </div>
      </div>
    </>
  );
}

function StepPerson({ data, upd, errors }) {
  const showFreeWarn = isValidEmail(data.email) && isFreeEmail(data.email);
  return (
    <>
      <StepHeader step={2} total={7} icon={User} title="Authorized person"
        subtitle="The person who can sign on behalf of your company." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Authorized person name" required error={errors.auth_person_name}>
          <Input value={data.auth_person_name} onChange={(e)=>upd({auth_person_name:e.target.value})}
            placeholder="Full name" className="h-11 rounded-xl" data-testid="auth-name-input" />
        </Field>
        <Field label="Mobile number" required error={errors.mobile}>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={data.mobile} onChange={(e)=>upd({mobile:e.target.value})}
              placeholder="+91 98765 43210" className="h-11 rounded-xl pl-9" data-testid="mobile-input" />
          </div>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Official email address" required error={errors.email}
            hint="Use your domain email (e.g. you@yourcompany.com) for faster Meta approval.">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={data.email} onChange={(e)=>upd({email:e.target.value})}
                placeholder="you@yourcompany.com" className="h-11 rounded-xl pl-9" data-testid="email-input" />
            </div>
          </Field>
          {showFreeWarn && (
            <div className="mt-3 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800" data-testid="free-email-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p className="text-xs leading-relaxed">
                <b>Heads up:</b> We recommend using your official company email for faster Meta verification. You can still continue with this email.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function StepNumber({ data, upd, errors }) {
  return (
    <>
      <StepHeader step={3} total={7} icon={Phone} title="Which number would you like to use?"
        subtitle="You can bring your own number or get a new virtual number from Wappit." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button type="button" onClick={()=>upd({number_option:"existing"})}
          className={cls("text-left rounded-2xl border-2 p-5 transition-all",
            data.number_option==="existing" ? "border-emerald-500 bg-emerald-50/50 shadow-sm" : "border-slate-200 hover:border-slate-300 bg-white")}
          data-testid="number-option-existing">
          <div className="flex items-center gap-2 text-emerald-700 text-xs font-bold uppercase tracking-wider">
            <Phone className="h-3.5 w-3.5" /> Option A
          </div>
          <h3 className="mt-2 text-lg font-bold text-slate-900">I already have a phone number</h3>
          <p className="mt-1 text-sm text-slate-600">Use an existing landline or mobile number for the API.</p>
        </button>
        <button type="button" onClick={()=>upd({number_option:"virtual"})}
          className={cls("text-left rounded-2xl border-2 p-5 transition-all",
            data.number_option==="virtual" ? "border-emerald-500 bg-emerald-50/50 shadow-sm" : "border-slate-200 hover:border-slate-300 bg-white")}
          data-testid="number-option-virtual">
          <div className="flex items-center gap-2 text-blue-700 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="h-3.5 w-3.5" /> Option B
          </div>
          <h3 className="mt-2 text-lg font-bold text-slate-900">I need a new Virtual Number</h3>
          <p className="mt-1 text-sm text-slate-600">Wappit will provision a fresh virtual number for your API account.</p>
        </button>
      </div>

      {data.number_option === "existing" ? (
        <div className="mt-6 space-y-4">
          <Field label="WhatsApp number" required error={errors.whatsapp_number}>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={data.whatsapp_number} onChange={(e)=>upd({whatsapp_number:e.target.value})}
                placeholder="+91 98765 43210" className="h-11 rounded-xl pl-9" data-testid="whatsapp-number-input" />
            </div>
          </Field>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm flex items-start gap-3">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p><b>Important:</b> This number must NOT currently be active on WhatsApp or WhatsApp Business App. It should be removed from those apps before onboarding.</p>
          </div>
        </div>
      ) : (
        <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-900 text-sm flex items-start gap-3">
          <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p>Wappit can provide a new Virtual Number for your WhatsApp Business API account. Our team will assign one after your application is approved.</p>
        </div>
      )}
    </>
  );
}

function StepDisplay({ data, upd, errors }) {
  return (
    <>
      <StepHeader step={4} total={7} icon={MessageSquare} title="Display name"
        subtitle="How your business will appear inside WhatsApp." />
      <Field label="Display name" required error={errors.display_name}
        hint="Should closely match your company name, website or legal documents to maximise Meta approval.">
        <Input value={data.display_name} onChange={(e)=>upd({display_name:e.target.value})}
          placeholder="Wappit · WhatsApp Business" className="h-11 rounded-xl" data-testid="display-name-input" />
      </Field>
      {/* Preview card */}
      <div className="mt-6 rounded-2xl bg-gradient-to-br from-slate-900 to-emerald-900 text-white p-4 max-w-md">
        <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Live preview</div>
        <div className="mt-2 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-emerald-400 text-slate-900 font-bold flex items-center justify-center">
            {(data.display_name || "W").trim().charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-semibold">{data.display_name || "Your Display Name"}</div>
            <div className="text-[11px] text-emerald-200 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Business · Online</div>
          </div>
        </div>
      </div>
    </>
  );
}

function StepBusiness({ data, upd }) {
  return (
    <>
      <StepHeader step={5} total={7} icon={Briefcase} title="Business details"
        subtitle="Helps us craft a more accurate Meta application." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Business category">
          <Select value={data.business_category || ""} onValueChange={(v)=>upd({business_category:v})}>
            <SelectTrigger className="h-11 rounded-xl" data-testid="business-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
            <SelectContent>
              {BUSINESS_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Facebook Business Page URL">
          <Input value={data.facebook_url} onChange={(e)=>upd({facebook_url:e.target.value})}
            placeholder="https://facebook.com/yourpage" className="h-11 rounded-xl" data-testid="facebook-input" />
        </Field>
        <Field label="Instagram Profile URL">
          <Input value={data.instagram_url} onChange={(e)=>upd({instagram_url:e.target.value})}
            placeholder="https://instagram.com/yourhandle" className="h-11 rounded-xl" data-testid="instagram-input" />
        </Field>
        <Field label="Do you have a Meta Business Manager?">
          <div className="grid grid-cols-3 gap-2">
            {[{v:"yes",l:"Yes"},{v:"no",l:"No"},{v:"not_sure",l:"Not sure"}].map((o) => (
              <button key={o.v} type="button" onClick={()=>upd({has_meta_business_manager:o.v})}
                className={cls("rounded-xl px-3 py-2 text-sm font-semibold transition-colors",
                  data.has_meta_business_manager===o.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200")}
                data-testid={`mbm-${o.v}`}>{o.l}</button>
            ))}
          </div>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Business description">
            <Textarea value={data.business_description} onChange={(e)=>upd({business_description:e.target.value})}
              placeholder="A short paragraph about what your business does (max 2000 chars)." className="rounded-xl min-h-[100px]" data-testid="business-description-input" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Preferred use case">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {USE_CASES.map((o) => (
                <button key={o.v} type="button" onClick={()=>upd({use_case:o.v})}
                  className={cls("rounded-xl px-3 py-2 text-sm font-semibold transition-colors",
                    data.use_case===o.v ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200")}
                  data-testid={`usecase-${o.v}`}>{o.l}</button>
              ))}
            </div>
          </Field>
        </div>
      </div>
    </>
  );
}

function StepDocuments({ files, onAdd, onRemove, errors, meta }) {
  const [selectedType, setSelectedType] = useState("");
  const inputRef = React.useRef();
  const [dragOver, setDragOver] = useState(false);

  const uploadedTypes = files.filter(f => f.id && !f.error).map(f => f.type);
  const hasMandatory = uploadedTypes.some(t => MANDATORY.includes(t));
  const totalCount = uploadedTypes.length;

  const onPick = (filesList) => {
    onAdd(filesList, selectedType);
    // Reset file input so the same file can be re-selected
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <>
      <StepHeader step={6} total={7} icon={FileCheck} title="Upload documents"
        subtitle="Minimum 3 documents. GST or MSME is mandatory + any two others." />

      {/* Rule banner */}
      <div className="rounded-2xl bg-slate-900 text-white p-5 mb-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-emerald-300">
          <FileText className="h-3.5 w-3.5" /> Upload rules
        </div>
        <ul className="mt-2 grid sm:grid-cols-3 gap-3 text-sm">
          <li className={cls("rounded-lg px-3 py-2 ring-1", hasMandatory ? "bg-emerald-500/10 ring-emerald-400/30 text-emerald-200" : "bg-amber-500/10 ring-amber-400/30 text-amber-200")}>
            {hasMandatory ? "✓" : "•"} GST Certificate <i>or</i> MSME Certificate (1 required)
          </li>
          <li className={cls("rounded-lg px-3 py-2 ring-1", totalCount >= 3 ? "bg-emerald-500/10 ring-emerald-400/30 text-emerald-200" : "bg-slate-800 ring-white/10 text-slate-300")}>
            {totalCount >= 3 ? "✓" : "•"} Plus any 2 supporting documents
          </li>
          <li className="rounded-lg px-3 py-2 ring-1 bg-slate-800 ring-white/10 text-slate-300">
            {`Max ${meta?.max_file_mb || 10} MB · PDF, PNG, JPG, JPEG, WEBP`}
          </li>
        </ul>
      </div>

      {/* Dropzone */}
      <div
        onDragOver={(e)=>{e.preventDefault();setDragOver(true);}}
        onDragLeave={()=>setDragOver(false)}
        onDrop={(e)=>{e.preventDefault();setDragOver(false);onPick(e.dataTransfer.files);}}
        className={cls("rounded-2xl border-2 border-dashed p-6 transition-colors",
          dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-slate-50")}
        data-testid="dropzone"
      >
        <div className="grid sm:grid-cols-[1fr_auto] items-end gap-4">
          <div>
            <Label className="text-sm font-semibold text-slate-800">Document type</Label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="h-11 rounded-xl mt-1.5 bg-white" data-testid="doc-type-select"><SelectValue placeholder="Select document type to upload" /></SelectTrigger>
              <SelectContent>
                {(meta?.document_types || Object.keys(DOC_LABELS)).map((k) => (
                  <SelectItem key={k} value={k}>
                    {DOC_LABELS[k] || k}{MANDATORY.includes(k) ? " · Mandatory option" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={()=>inputRef.current?.click()} disabled={!selectedType}
            className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="upload-btn">
            <UploadCloud className="h-4 w-4 mr-1.5" /> Choose file
          </Button>
        </div>
        <p className="mt-3 text-xs text-slate-600 text-center">…or drag &amp; drop files anywhere in this area.</p>
        <input ref={inputRef} type="file" hidden multiple accept=".pdf,.png,.jpg,.jpeg,.webp"
          onChange={(e)=>onPick(e.target.files)} data-testid="file-input" />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <ul className="mt-5 divide-y divide-slate-100 rounded-2xl bg-white border border-slate-200" data-testid="file-list">
          {files.map((f) => (
            <li key={f.localId} className="px-4 py-3 flex items-center gap-3">
              <div className={cls("h-9 w-9 rounded-lg flex items-center justify-center",
                f.error ? "bg-rose-50 text-rose-600" : f.uploading ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600")}>
                {f.error ? <AlertTriangle className="h-4 w-4" /> : f.uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">{f.file_name}</div>
                <div className="text-xs text-slate-500">{DOC_LABELS[f.type] || f.type} · {fmtBytes(f.size)}{f.error?` · ${f.error}`:""}</div>
              </div>
              <button onClick={()=>onRemove(f.localId)} className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center" data-testid={`remove-${f.localId}`}>
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {errors._docs && <p className="mt-3 text-sm text-rose-600 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> {errors._docs}</p>}
    </>
  );
}

function StepConfirm({ data, files, upd, errors }) {
  const docs = files.filter(f => f.id && !f.error);
  return (
    <>
      <StepHeader step={7} total={7} icon={ShieldCheck} title="Review &amp; submit"
        subtitle="Double-check everything below. You can go back to fix anything." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Summary title="Company" rows={[
          ["Company name", data.company_name],
          ["Company type", data.company_type],
          ["Website", data.company_website],
          ["GST", data.gst_number || "—"],
        ]}/>
        <Summary title="Authorized person" rows={[
          ["Name", data.auth_person_name],
          ["Mobile", data.mobile],
          ["Email", data.email],
        ]}/>
        <Summary title="WhatsApp" rows={[
          ["Plan", data.number_option === "existing" ? "Bringing existing number" : "New virtual number"],
          ["Number", data.whatsapp_number || (data.number_option === "virtual" ? "Will be assigned" : "—")],
          ["Display name", data.display_name],
        ]}/>
        <Summary title="Business" rows={[
          ["Category", data.business_category || "—"],
          ["Meta Business Manager", data.has_meta_business_manager.replace("_"," ")],
          ["Use case", data.use_case.replace("_"," ")],
        ]}/>
      </div>
      <div className="mt-4 rounded-2xl bg-slate-50 border border-slate-200 p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Uploaded documents ({docs.length})</div>
        <ul className="grid sm:grid-cols-2 gap-2">
          {docs.map((d) => (
            <li key={d.localId} className="flex items-center gap-2 text-sm text-slate-700">
              <FileText className="h-4 w-4 text-emerald-600" />
              <span className="font-semibold">{DOC_LABELS[d.type] || d.type}</span>
              <span className="text-slate-400 truncate">· {d.file_name}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 space-y-3">
        <Check label="I confirm that all information provided is correct."
          checked={data.confirmed_info} onChange={(v)=>upd({confirmed_info:v})}
          error={errors.confirmed_info} testId="confirm-info-checkbox" />
        <Check label="I authorize Promotion Kart / Wappit to complete WhatsApp Business API onboarding on my behalf."
          checked={data.authorized} onChange={(v)=>upd({authorized:v})}
          error={errors.authorized} testId="confirm-auth-checkbox" />
      </div>
    </>
  );
}

const Summary = ({ title, rows }) => (
  <div className="rounded-2xl border border-slate-200 p-4 bg-white">
    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{title}</div>
    <dl className="space-y-1.5">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-start justify-between gap-3 text-sm">
          <dt className="text-slate-500">{k}</dt>
          <dd className="text-slate-900 font-semibold text-right truncate max-w-[60%]">{v || "—"}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const Check = ({ label, checked, onChange, error, testId }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e)=>onChange(e.target.checked)}
      className="mt-1 h-4 w-4 accent-emerald-600" data-testid={testId} />
    <span className={cls("text-sm leading-relaxed", error ? "text-rose-600" : "text-slate-700")}>{label}</span>
  </label>
);

/* ---------- success page ---------- */
function SuccessView({ refId, onHome }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="onboarding-success">
      <header className="border-b border-slate-200/70 bg-white/70 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-xl text-center">
          <div className="mx-auto h-20 w-20 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center shadow-[0_8px_30px_rgba(16,185,129,0.35)]">
            <CheckCircle2 className="h-10 w-10" />
          </div>
          <h1 className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Thank you!
          </h1>
          <p className="mt-2 text-slate-600">Your onboarding request has been submitted successfully.</p>

          <div className="mt-7 rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm">
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Reference ID</div>
            <div className="mt-1 text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="success-ref-id">{refId}</div>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500">Status</span>
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100">Pending Review</span>
            </div>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">
              Our onboarding team will review your application and contact you shortly. Most applications are processed within 24&ndash;72 hours.
            </p>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={onHome} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="back-home-btn">
              <Home className="h-4 w-4 mr-1.5" /> Back to home
            </Button>
            <a href="mailto:hello@wappit.in" className="text-sm font-semibold text-emerald-700 hover:underline flex items-center gap-1" data-testid="contact-support">
              Contact support <ChevronRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
